import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { Auth } from 'aws-amplify';

import {
  Button,
  Checkbox,
  message,
  Modal,
  Result,
  Select,
  Space,
  Spin,
} from 'antd';

import axios from 'axios';
import { useRequest } from 'ahooks';

import { ScissorOutlined } from '@ant-design/icons';
import QrScanner from 'qr-scanner';
import * as pdfjsLib from 'pdfjs-dist';
import { ShortURLResponse } from './AlignmentsTable';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

const { Option } = Select;

type TemplateCategory = 'SVA' | 'HEDR' | 'NPS';
const TEMPLATE_OPTIONS: TemplateCategory[] = ['SVA', 'HEDR', 'NPS'];

export interface AlignmentPDFPreviewRef {
  openSplitModal: () => void;
  isReady: boolean;
}

interface AlignmentPDFPreviewProps {
  submission_id: string;
  submission_source: string;
  onedrive_file_id?: string | null;
  onQrCodeDetection?: (qrCode: (ShortURLResponse | null)[]) => void;
  onReady?: () => void;
  onSplitComplete?: () => void;
  hideSplitButton?: boolean;
  iframeHeight?: number;
  moveOriginalAfterSplit?: boolean;
}

const AlignmentPDFPreview = forwardRef<
  AlignmentPDFPreviewRef,
  AlignmentPDFPreviewProps
>(
  (
    {
      submission_id,
      submission_source,
      onedrive_file_id,
      onQrCodeDetection,
      onReady,
      onSplitComplete,
      hideSplitButton,
      iframeHeight = 800,
      moveOriginalAfterSplit,
    },
    ref
  ) => {
    const [pagePreviews, setPagePreviews] = useState<string[]>([]);
    const [qrCodesInPages, setQrCodesInPages] = useState<
      (ShortURLResponse | null)[]
    >([]);
    const [selectedPages, setSelectedPages] = useState<boolean[]>([]);
    const [selectedTemplateVersions, setSelectedTemplateVersions] = useState<
      (TemplateCategory | null)[]
    >([]);
    const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
    const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);
    const [isSubmittingSplit, setIsSubmittingSplit] = useState(false);

    const fetchPdfFromBackend = async () => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();

      const params = new URLSearchParams();
      if (onedrive_file_id) {
        params.set('onedrive_file_id', onedrive_file_id);
      } else {
        params.set('submission_id', submission_id);
        params.set('submission_source', submission_source);
      }

      const response = await axios.get(
        `${
          process.env.REACT_APP_CHALICE_URL
        }/get_onedrive_pdf?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Decode base64 to ArrayBuffer
      const binaryString = atob(response.data.pdf_base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return {
        pdfArrayBuffer: bytes.buffer,
        onedriveFileId: response.data.onedrive_file_id,
      };
    };

    const pdfRequest = useRequest(fetchPdfFromBackend, {
      cacheKey: onedrive_file_id || `${submission_source}:${submission_id}`,
      onSuccess: async (result) => {
        const parsedQRCodes = await getQRCodesInPages(result.pdfArrayBuffer);
        setPagePreviews([]);
        setQrCodesInPages(parsedQRCodes);
        setSelectedPages(parsedQRCodes.map(hasMabelQRCode));
        setSelectedTemplateVersions(parsedQRCodes.map(resolveTemplateCategory));
        if (onQrCodeDetection) onQrCodeDetection(parsedQRCodes);
        if (onReady) onReady();
      },
    });

    const effectiveOneDriveFileId =
      pdfRequest.data?.onedriveFileId || onedrive_file_id;

    const isValidHttpsUrl = (s: string) => {
      let url;
      try {
        url = new URL(s);
      } catch (_) {
        return false;
      }
      return url.protocol === 'https:';
    };

    const getQRCodesInPages = async (
      pdfBlob: ArrayBuffer
    ): Promise<(ShortURLResponse | null)[]> => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();

      const pdf = await pdfjsLib.getDocument({
        data: pdfBlob,
        isEvalSupported: false,
      }).promise;
      const qrCodesInPages = await Promise.all(
        Array.from({ length: pdf.numPages }, async (_, i) => {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 1 });
          const canvas = new OffscreenCanvas(viewport.width, viewport.height);
          await page.render({
            canvasContext: canvas.getContext('2d')!,
            viewport: viewport,
          }).promise;
          const code = await QrScanner.scanImage(canvas, {
            returnDetailedScanResult: true,
          }).catch(() => {
            return { data: null };
          });
          if (!code.data || !isValidHttpsUrl(code.data)) {
            return null;
          }
          const response = await axios.get(
            `${process.env.REACT_APP_CHALICE_URL}/parse_short_url?short_url=${code.data}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return response.data;
        })
      );
      return qrCodesInPages;
    };

    const resolveTemplateCategory = (
      code: ShortURLResponse | null
    ): TemplateCategory | null => {
      const templateVersion =
        code?.query_params?.template_version ||
        code?.query_params?.dce_form_ycode ||
        '';
      if (!templateVersion) return null;
      if (
        templateVersion.startsWith('MPVA') ||
        templateVersion.startsWith('MSVA') ||
        templateVersion.startsWith('SVA')
      ) {
        return 'SVA';
      }
      if (templateVersion.startsWith('HEDR')) {
        return 'HEDR';
      }
      if (templateVersion.startsWith('NPS')) {
        return 'NPS';
      }
      return null;
    };

    const hasMabelQRCode = (code: ShortURLResponse | null) =>
      !!code && Object.keys(code.query_params || {}).length > 0;

    const generatePagePreviews = async (pdfBlob: ArrayBuffer) => {
      setIsGeneratingPreviews(true);
      try {
        const pdf = await pdfjsLib.getDocument({
          data: pdfBlob,
          isEvalSupported: false,
        }).promise;
        const previews: string[] = [];
        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 0.35 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            previews.push('');
            continue;
          }
          await page.render({ canvasContext: ctx, viewport }).promise;
          previews.push(canvas.toDataURL('image/png'));
        }
        setPagePreviews(previews);
      } finally {
        setIsGeneratingPreviews(false);
      }
    };

    const openSplitModal = useCallback(() => {
      if (!pdfRequest.data?.pdfArrayBuffer) {
        message.error('PDF not ready yet.');
        return;
      }
      setIsSplitModalOpen(true);
      if (!pagePreviews.length) {
        void generatePagePreviews(pdfRequest.data.pdfArrayBuffer);
      }
    }, [pdfRequest.data, pagePreviews.length]);

    const isReady = !!pdfRequest.data && !!effectiveOneDriveFileId;
    useImperativeHandle(ref, () => ({ openSplitModal, isReady }), [
      isReady,
      openSplitModal,
    ]);

    const updatePageSelection = (index: number, checked: boolean) => {
      setSelectedPages((prev) => {
        const next =
          prev.length > 0
            ? [...prev]
            : Array(
                Math.max(pagePreviews.length, qrCodesInPages.length, index + 1)
              ).fill(false);
        next[index] = checked;
        return next;
      });
      if (checked) {
        setSelectedTemplateVersions((prev) => {
          const next =
            prev.length > 0
              ? [...prev]
              : Array(
                  Math.max(
                    pagePreviews.length,
                    qrCodesInPages.length,
                    index + 1
                  )
                ).fill(null);
          if (!next[index]) {
            next[index] = resolveTemplateCategory(qrCodesInPages[index]);
          }
          return next;
        });
      }
    };

    const updateTemplateSelection = (
      index: number,
      value: TemplateCategory
    ) => {
      setSelectedTemplateVersions((prev) => {
        const next =
          prev.length > 0
            ? [...prev]
            : Array(
                Math.max(pagePreviews.length, qrCodesInPages.length, index + 1)
              ).fill(null);
        next[index] = value;
        return next;
      });
    };

    const submitSplitSelectedPages = async () => {
      if (!effectiveOneDriveFileId) {
        message.error('Missing OneDrive file id.');
        return;
      }
      const selectedPageNumbers = selectedPages
        .map((isSelected, index) => (isSelected ? index + 1 : null))
        .filter((value): value is number => value !== null);
      if (selectedPageNumbers.length === 0) {
        message.warning('Select at least one page to split.');
        return;
      }
      setIsSubmittingSplit(true);
      try {
        const token = (await Auth.currentSession()).getIdToken().getJwtToken();
        await Promise.all(
          selectedPageNumbers.map((selectedPage, idx) => {
            const templateVersion = selectedTemplateVersions[selectedPage - 1]!;
            // Only move original on the last request to avoid race conditions
            const shouldMoveOriginal =
              moveOriginalAfterSplit && idx === selectedPageNumbers.length - 1;
            return axios.post(
              `${process.env.REACT_APP_CHALICE_URL}/split_and_save_onedrive_selected_pages`,
              {
                onedrive_file_id: effectiveOneDriveFileId,
                submission_source,
                selected_page: selectedPage,
                template_version: templateVersion,
                move_original: shouldMoveOriginal,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          })
        );
        message.success(
          `${selectedPageNumbers.length} page${
            selectedPageNumbers.length > 1 ? 's' : ''
          } created`
        );
        setIsSplitModalOpen(false);
        if (onSplitComplete) onSplitComplete();
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || error?.message || 'Split failed.'
        );
      } finally {
        setIsSubmittingSplit(false);
      }
    };

    // Create blob URL for PDF display
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

    useEffect(() => {
      if (pdfRequest.data?.pdfArrayBuffer) {
        const blob = new Blob([pdfRequest.data.pdfArrayBuffer], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(`${url}#toolbar=0&view=FitH`);
        return () => URL.revokeObjectURL(url);
      }
    }, [pdfRequest.data]);

    return (
      <Spin spinning={pdfRequest.loading}>
        {pdfRequest.error ? (
          <Result
            status="error"
            title="Error retrieving file from OneDrive"
            subTitle={pdfRequest.error.message}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                style={{
                  width: '100%',
                  height: iframeHeight,
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                }}
                title="PDF Preview"
              />
            ) : (
              <div
                style={{
                  height: iframeHeight,
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                }}
              >
                Loading PDF...
              </div>
            )}
            {!hideSplitButton && (
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
              >
                <Button
                  type="primary"
                  disabled={!pdfRequest.data || !effectiveOneDriveFileId}
                  onClick={openSplitModal}
                  icon={<ScissorOutlined />}
                >
                  Split or Move Pages
                </Button>
              </div>
            )}
            <Modal
              title="Split or Move Pages"
              visible={isSplitModalOpen}
              onCancel={() => setIsSplitModalOpen(false)}
              width={960}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => setIsSplitModalOpen(false)}
                  disabled={isSubmittingSplit}
                >
                  Cancel
                </Button>,
                <Button
                  key="split"
                  type="primary"
                  loading={isSubmittingSplit}
                  disabled={
                    !selectedPages.some(Boolean) ||
                    selectedPages.some(
                      (isSelected, i) =>
                        isSelected && !selectedTemplateVersions[i]
                    )
                  }
                  onClick={submitSplitSelectedPages}
                  icon={<ScissorOutlined />}
                >
                  Split or Move Selected Pages
                </Button>,
              ]}
            >
              <Spin spinning={isGeneratingPreviews}>
                {pagePreviews.length ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: 8,
                        marginBottom: 12,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedPages(pagePreviews.map(() => true));
                          setSelectedTemplateVersions((prev) =>
                            prev.length > 0
                              ? prev
                              : pagePreviews.map((_, index) =>
                                  resolveTemplateCategory(qrCodesInPages[index])
                                )
                          );
                        }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setSelectedPages(pagePreviews.map(() => false))
                        }
                      >
                        Unselect All
                      </Button>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: 12,
                      }}
                    >
                      {pagePreviews.map((preview, index) => (
                        <div
                          key={`page-preview-${index + 1}`}
                          style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                            padding: 8,
                          }}
                        >
                          <Checkbox
                            checked={!!selectedPages[index]}
                            onChange={(event) =>
                              updatePageSelection(index, event.target.checked)
                            }
                          >
                            Page {index + 1}
                          </Checkbox>
                          <div style={{ marginTop: 8 }}>
                            <img
                              alt={`Page ${index + 1}`}
                              src={preview}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                              }}
                            />
                          </div>
                          {selectedPages[index] ? (
                            <div style={{ marginTop: 8 }}>
                              <Select
                                size="small"
                                placeholder="Select category"
                                value={
                                  selectedTemplateVersions[index] ??
                                  resolveTemplateCategory(
                                    qrCodesInPages[index]
                                  ) ??
                                  undefined
                                }
                                onChange={(value) =>
                                  updateTemplateSelection(
                                    index,
                                    value as TemplateCategory
                                  )
                                }
                                style={{ width: '100%' }}
                              >
                                {TEMPLATE_OPTIONS.map((option) => (
                                  <Option key={option} value={option}>
                                    {option}
                                  </Option>
                                ))}
                              </Select>
                              {!selectedTemplateVersions[index] && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: '#ff4d4f',
                                    marginTop: 2,
                                  }}
                                >
                                  Required
                                </div>
                              )}
                            </div>
                          ) : hasMabelQRCode(qrCodesInPages[index]) ? (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: '#389e0d',
                              }}
                            >
                              QR detected
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    Generating previews...
                  </div>
                )}
              </Spin>
            </Modal>
          </Space>
        )}
      </Spin>
    );
  }
);
export default AlignmentPDFPreview;
